import React, { useEffect } from 'react';
import AppLayout from '../Layout/AppLayout';
import ActionPlanChecklist from '../Dashboard/ActionPlanChecklist';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

function getTierColor(tier) {
  switch (tier) {
    case 'RED FLAG':
      return 'bg-red-500/10 border-red-500/20 text-red-400';
    case 'AVERAGE':
      return 'bg-amber-500/10 border-amber-500/20 text-amber-400';
    case 'GREEN ZONE':
      return 'bg-green-500/10 border-green-500/20 text-green-400';
    case 'BEST-IN-CLASS':
      return 'bg-[hsl(var(--chart-2))]/10 border-[hsl(var(--chart-2))]/20 text-[hsl(var(--chart-2))]';
    default:
      return 'border-border text-muted-foreground';
  }
}

function ValueAccelerationScorecard({ scorecard }) {
  const assessment = scorecard.assessment || {};
  const valuation = scorecard.valuation || {};
  const wealth = scorecard.wealth || {};
  const action_items = scorecard.action_items || [];
  const total_recoverable = scorecard.total_recoverable || 0;

  const rawScore = assessment.score || 0;
  const assessmentScore = Math.round(rawScore <= 1 ? rawScore * 100 : rawScore);
  const tier = assessment.tier || 'AVERAGE';
  const currentMultiple = valuation.current_multiple || 0;
  const rangeLow = valuation.range_low || 0;
  const rangeHigh = valuation.range_high || 0;
  const currentValue = valuation.current_value || 0;
  const potentialValue = valuation.potential_value || 0;
  const valueGap = potentialValue - currentValue;

  const wealthGoal = wealth.goal || null;
  const currentNetWorth = wealth.current_assets || null;
  const wealthGap = wealth.gap || null;

  const rangeSpan = rangeHigh - rangeLow;
  const markerPosition = rangeSpan > 0
    ? Math.max(0, Math.min(100, ((currentMultiple - rangeLow) / rangeSpan) * 100))
    : 50;

  const gapClosablePercentage = valueGap > 0
    ? Math.min((total_recoverable / valueGap) * 100, 100)
    : 0;
  const isGapFullyClosable = total_recoverable >= valueGap;

  return (
    <div className="mb-8">
      {/* Section Label */}
      <div className="flex items-center gap-2 mb-6">
        <div className="w-2 h-2 rounded-full bg-[hsl(var(--succeedo-orange))] shadow-[0_0_8px_hsl(var(--succeedo-orange))]" />
        <span className="font-mono text-[11px] uppercase tracking-wide text-muted-foreground/40">
          VALUE ACCELERATION SCORECARD
        </span>
      </div>

      {/* Main Card */}
      <div className="bg-card border border-border rounded-[16px] p-6">
        {/* Row 1: Score Context Bar */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <span className="text-[16px] text-foreground font-medium">
                Assessment Score: <span className="font-semibold">{assessmentScore}%</span>
              </span>
              <span className={`rounded-full px-3 py-1 font-mono text-[11px] uppercase border ${getTierColor(tier)}`}>
                {tier}
              </span>
            </div>
            <div className="text-[14px] text-muted-foreground">
              Your Multiple: <span className="text-foreground font-semibold">{currentMultiple}x</span> of {rangeLow}x&ndash;{rangeHigh}x range
            </div>
          </div>

          {/* Range Bar */}
          <div className="relative h-2 rounded-full bg-border">
            <div className="absolute inset-0 rounded-full bg-gradient-to-r from-red-400 via-amber-400 via-green-400 to-[hsl(var(--chart-2))]" />
            <div
              className="absolute w-3 h-3 rounded-full bg-foreground border-2 border-background -top-0.5"
              style={{ left: `calc(${markerPosition}% - 6px)` }}
            />
          </div>
        </div>

        {/* Row 2: Gap Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {/* Value Gap Card */}
          <div className="bg-muted/30 rounded-[12px] p-5">
            <div className="font-mono text-[10px] uppercase text-muted-foreground/50 tracking-wide mb-3">
              VALUE GAP
            </div>
            {valuation.current_value != null ? (
              <React.Fragment>
                <div className="space-y-2 mb-3">
                  <div>
                    <div className="text-[12px] text-muted-foreground mb-1">Current Value</div>
                    <div className="text-[28px] font-light text-foreground">
                      {formatCurrency(currentValue)}
                    </div>
                  </div>
                  <div>
                    <div className="text-[12px] text-muted-foreground mb-1">Potential Value</div>
                    <div className="text-[28px] font-light text-[hsl(var(--chart-2))]">
                      {formatCurrency(potentialValue)}
                    </div>
                  </div>
                </div>
                <div className="h-px bg-border my-3" />
                <div className="text-[16px] text-[hsl(var(--succeedo-orange))] font-medium">
                  Gap: {formatCurrency(valueGap)} ({currentValue > 0 ? Math.round((valueGap / currentValue) * 100) : 0}%)
                </div>
              </React.Fragment>
            ) : (
              <div className="flex items-center justify-center h-full min-h-[200px]">
                <a
                  href="/succeedo/valuation"
                  className="border border-[hsl(var(--succeedo-orange))]/30 text-[hsl(var(--succeedo-orange))] rounded-full px-4 py-2 text-[13px] hover:bg-[hsl(var(--succeedo-orange))]/5 transition-colors"
                >
                  Run your valuation
                </a>
              </div>
            )}
          </div>

          {/* Wealth Gap Card */}
          <div className="bg-muted/30 rounded-[12px] p-5">
            <div className="font-mono text-[10px] uppercase text-muted-foreground/50 tracking-wide mb-3">
              WEALTH GAP
            </div>
            {wealthGoal && currentNetWorth != null && wealthGap != null ? (
              <React.Fragment>
                <div className="space-y-2 mb-3">
                  <div>
                    <div className="text-[12px] text-muted-foreground mb-1">Post-Exit Goal</div>
                    <div className="text-[28px] font-light text-foreground">
                      {formatCurrency(wealthGoal)}
                    </div>
                  </div>
                  <div>
                    <div className="text-[12px] text-muted-foreground mb-1">
                      Current Net Worth
                      <span className="text-[11px] text-muted-foreground/40 ml-1">(excluding business)</span>
                    </div>
                    <div className="text-[28px] font-light text-foreground">
                      {formatCurrency(currentNetWorth)}
                    </div>
                  </div>
                </div>
                <div className="h-px bg-border my-3" />
                <div className="text-[16px] text-[hsl(var(--succeedo-orange))] font-medium">
                  Gap: {formatCurrency(wealthGap)}
                </div>
              </React.Fragment>
            ) : (
              <div className="flex flex-col items-center justify-center h-full min-h-[200px] gap-2">
                <p className="text-[13px] text-muted-foreground text-center">Complete your assessment to calculate your wealth gap.</p>
                <a
                  href="/succeedo/assessment"
                  className="border border-[hsl(var(--succeedo-orange))]/30 text-[hsl(var(--succeedo-orange))] rounded-full px-4 py-2 text-[13px] hover:bg-[hsl(var(--succeedo-orange))]/5 transition-colors"
                >
                  Retake assessment &rarr;
                </a>
              </div>
            )}
          </div>
        </div>

        {/* Row 3: Action Plan Impact (only when valuation data exists) */}
        {action_items.length > 0 && <div>
          <div className="font-mono text-[10px] uppercase text-muted-foreground/50 tracking-wide mb-4">
            ACTION PLAN IMPACT
          </div>
          <div className="space-y-3 mb-4">
            {action_items.map((item, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-[hsl(var(--succeedo-orange))]" style={{ fontSize: '14px' }}>&#10022;</span>
                  <span className="text-[14px] text-foreground">{item.title}</span>
                </div>
                <span className="text-[14px] font-medium text-[hsl(var(--chart-2))]">
                  +{formatCurrency(item.value_impact)}
                </span>
              </div>
            ))}
          </div>
          <div className="h-px bg-border mb-3" />
          <div className="text-[14px] text-foreground">
            Total recoverable: <span className="font-semibold">{formatCurrency(total_recoverable)}</span> of {formatCurrency(valueGap)} gap
            {isGapFullyClosable ? (
              <span className="text-[hsl(var(--chart-2))] ml-2">(Gap fully closable)</span>
            ) : (
              <span className="text-muted-foreground ml-2">({Math.round(gapClosablePercentage)}% closable through action plan)</span>
            )}
          </div>
        </div>}
      </div>
    </div>
  );
}

export default function ActionPlanPage({
  scorecard,
  items = [],
  daysRemaining,
  sprintExpired,
  onStatusChange,
  onRequestIntroduction,
  introductionRequests,
  introLoading,
  persona = 'SELLER',
  userName,
  userInitials,
  planProgress,
  actionPlanBadge,
  isPartner,
  advisoryTeamMembers = [],
}) {
  useEffect(() => {
    if (typeof window !== 'undefined' && window.ahoy) {
      window.ahoy.track('succeedo_action_plan_viewed');
    }
  }, []);

  const showRetakeCta = sprintExpired || (daysRemaining != null && daysRemaining < 10);

  return (
    <AppLayout
      breadcrumb="ACTION PLAN"
      persona={persona}
      userName={userName}
      userInitials={userInitials}
      planProgress={planProgress}
      actionPlanBadge={actionPlanBadge}
    >
      <div className="p-6">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-2 h-2 rounded-full bg-[hsl(var(--succeedo-orange))]" />
            <span className="font-mono text-[11px] uppercase text-muted-foreground tracking-wide">
              ACTION PLAN
            </span>
          </div>
          <h1 className="font-serif text-[42px] font-light text-foreground tracking-[-0.02em] mb-2">
            Your 90-Day Action Plan
          </h1>
          <p className="text-[16px] text-muted-foreground">
            Prioritized steps to accelerate your business value and prepare for succession.
          </p>
        </div>

        {/* Section 1: Value Acceleration Scorecard */}
        {scorecard && (
          <ValueAccelerationScorecard
            scorecard={scorecard}
          />
        )}

        {/* Section 2: Sprint Progress + Full Action Plan Checklist */}
        <ActionPlanChecklist
          items={items}
          daysRemaining={daysRemaining}
          onStatusChange={onStatusChange}
          onRequestIntroduction={onRequestIntroduction}
          introductionRequests={introductionRequests}
          introLoading={introLoading}
          advisoryTeamMembers={advisoryTeamMembers}
        />

        {/* Section 3: Retake Assessment CTA */}
        {showRetakeCta && (
          <div className="bg-card border border-border rounded-[16px] p-6">
            <h3 className="text-[18px] font-semibold text-foreground mb-2">
              Ready to reassess?
            </h3>
            <p className="text-[14px] text-muted-foreground mb-4">
              See how your score has improved
            </p>
            <a
              href="/assessment"
              className="inline-block bg-[hsl(var(--succeedo-orange))] text-white rounded-full px-5 py-2 text-sm font-medium hover:opacity-90 transition-opacity"
            >
              Retake Assessment &rarr;
            </a>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
